import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Smartphone, CheckCircle, Zap, Users, Star } from "lucide-react";
import { api } from "../lib/api";

export default function BetaTesting() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    device_type: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDeviceTypeChange = (value: string) => {
    setFormData({
      ...formData,
      device_type: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    try {
      await api.submitForm("beta_testing", formData);

      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        device_type: "",
      });
    } catch (error: any) {
      console.error("Beta testing signup error:", error);
      setSubmitStatus("error");
      setErrorMessage(
        error.message ||
          "Failed to sign up for beta testing. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome to Beta Testing!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You're all set! We'll send you an email with download instructions
              and beta testing details soon.
            </p>
            <Button onClick={() => setSubmitStatus("idle")} className="w-full">
              Sign Up Another Person
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-4">
            <Smartphone className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Join Our Mobile Beta Program
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Be among the first to experience Ithopu on mobile. Help shape the
            future of mental math learning!
          </p>
        </div>

        {/* Benefits Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-2 border-transparent hover:border-blue-200 transition-colors">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Early Access</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Get the app before it's publicly available
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-transparent hover:border-indigo-200 transition-colors">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="font-semibold mb-2">Shape the Product</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your feedback directly influences development
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-transparent hover:border-purple-200 transition-colors">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold mb-2">Exclusive Perks</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Special benefits for beta testers
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Signup Form */}
        <Card className="border-2 border-blue-100">
          <CardHeader>
            <CardTitle>Sign Up for Beta Testing</CardTitle>
            <CardDescription>
              Fill out the form below and we'll send you download instructions
              when the beta is ready.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <p className="text-xs text-gray-500">
                  We'll send download instructions to this email
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="device_type">
                  Device Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.device_type}
                  onValueChange={handleDeviceTypeChange}
                  required
                >
                  <SelectTrigger id="device_type">
                    <SelectValue placeholder="Select your device" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ios">iOS (iPhone/iPad)</SelectItem>
                    <SelectItem value="android">Android</SelectItem>
                    <SelectItem value="both">Both iOS and Android</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {submitStatus === "error" && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="mr-2">Signing up...</span>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </>
                ) : (
                  <>
                    <Smartphone className="w-4 h-4 mr-2" />
                    Join Beta Program
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>What to expect:</strong> Once you sign up, we'll notify
                you via email when the beta app is ready for download. You'll
                receive instructions for installing the app and providing
                feedback.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
